import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Wrapper } from './style';

const RadialBarChart = ({ items, width = 900, height = 700, id = '' }) => {
  const ref = useRef();
  const ht = items?.length > 25 ? 500 : height

  useEffect(() => {
    // clear
    d3.select(ref.current).selectAll('*').remove();
    if (!items || items.length === 0) return;

    const sorted = [...items].sort((a, b) => a.value - b.value);
    const data = [{ name: "", value: 0, isDummy: true }, ...sorted]
      .map((d, i) => ({ ...d, key: String(i) }));

    const svg = d3.select(ref.current)
      .attr('width', width)
      .attr('height', ht)
      .style('overflow', 'visible');

    const innerRadius = 70;
    const outerRadius = Math.min(width, ht) / 2 - 55;
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2},${ht / 2})`);

    const roundUpToNearestTen = num => Math.ceil(num / 10) * 10;
    const maxValue = roundUpToNearestTen(d3.max(items, d => d.value));
    const numCircles = maxValue <= 5 ? maxValue : 5;
    const step = maxValue / numCircles;
    const gridValues = d3.range(0, numCircles + 1).map(i => Math.round(i * step));

    const x = d3.scaleBand()
      .domain(data.map(d => d.key))
      .range([0, 2 * Math.PI])
      .align(0);

    const y = d3.scaleLinear()
      .domain([0, maxValue])
      .range([innerRadius, outerRadius]);

    const computeValue = n => {
      if (n < 1) return 0;
      const v = 180 / (n + 1);
      return -(Math.round(v));
    };
    const rotationOffset = computeValue(items.length) * (Math.PI / 180);

    // grid circles
    g.selectAll('.grid-circle')
      .data(gridValues)
      .enter()
      .append('circle')
      .attr('class', 'grid-circle')
      .style('fill', 'none')
      .attr('r', d => y(d))
      .attr('stroke', '#D8D8D6')
      .attr('strokeWidth', '1');

    const arcGen = d3.arc()
      .innerRadius(innerRadius)
      .padAngle(0.03)
      .padRadius(innerRadius)
      .padRadius(outerRadius);

    // bars
    g.append('g')
      .selectAll('path')
      .data(data.filter(d => !d.isDummy))
      .enter()
      .append('path')
      .attr('fill', '#095BBD')
      .attr('d', d => arcGen({
        outerRadius: y(d.value),
        startAngle: x(d.key) + rotationOffset,
        endAngle: x(d.key) + x.bandwidth() + rotationOffset
      }))
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(200).attr('fill', '#007bff')
          .attr('d', arcGen({
            outerRadius: y(d.value) + 5,
            startAngle: x(d.key) + rotationOffset,
            endAngle: x(d.key) + x.bandwidth() + rotationOffset
          }));
      })
      .on('mouseout', function(event, d) {
        d3.select(this).transition().duration(200).attr('fill', '#0056b3')
          .attr('d', arcGen({
            outerRadius: y(d.value),
            startAngle: x(d.key) + rotationOffset,
            endAngle: x(d.key) + x.bandwidth() + rotationOffset
          }));
      });

    // logic based on length
    const doHorizontal = items.length > 20;
    const smallFont = items.length > 30;
    const fontSize = smallFont ? '11px' : '12px';

    const gap = 5;

    const labelG = g.append('g')
      .selectAll('g')
      .data(data.filter(d => !d.isDummy))
      .enter()
      .append('g');

    labelG.each(function(d, i) {
      const angle = (x(d.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
      const lineStart = innerRadius;
      const lineEnd = outerRadius + 20;

      const x1 = Math.cos(angle) * lineStart;
      const y1 = Math.sin(angle) * lineStart;
      const x2 = Math.cos(angle) * lineEnd;
      const y2 = Math.sin(angle) * lineEnd;

      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const EPS = 0.2;
      let anchor = 'middle';
      if (cosA > EPS) anchor = 'start';
      else if (cosA < -EPS) anchor = 'end';
      if (Math.abs(cosA) <= EPS && sinA > 0) anchor = 'middle';

      let x3 = x2;
      const horizontalLength = (i % 2 === 0) ? 100 : 10;

      if (doHorizontal) {
        if (anchor === 'start') x3 = x2 + horizontalLength;
        else if (anchor === 'end') x3 = x2 - horizontalLength;
      }

      // radial line
      d3.select(this).append('line')
        .attr('class', 'dashed-line-radial')
        .style('stroke', '#000')
        .style('stroke-dasharray', '4')
        .attr('x1', x1).attr('y1', y1)
        .attr('x2', x2).attr('y2', y2)
        .style('fill', 'none');

      if (doHorizontal) {
        d3.select(this).append('line')
          .attr('class', 'dashed-line-horizontal')
          .style('stroke', '#000')
          .style('stroke-dasharray', '4')
          .attr('x1', x2).attr('y1', y2)
          .attr('x2', x3).attr('y2', y2)
          .style('fill', 'none');
      }

      // Text logic
      if (doHorizontal) {
        // one line: label + percent together
        d3.select(this).append('text')
          .attr('class', 'label-percent')
          .attr('fill', '#454545')
          .attr('x', x3)
          .attr('y', y2)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .text(`${d.name} ${d.value}%`);
      } else {
        // two lines: label, then percent beneath
        d3.select(this).append('text')
          .attr('class', 'label')
          .attr('fill', '#454545')
          .attr('x', x3)
          .attr('y', y2 + gap)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .text(d.name);

        d3.select(this).append('text')
          .attr('class', 'percent')
          .attr('fill', '#095BBD')
          .attr('x', x3)
          .attr('y', y2 + gap + 16)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .attr('font-weight', 600)
          .text(`${d.value}%`);
      }
    });

    // Grid value labels etc
    const dummy = data.find(d => d.isDummy);
    if (dummy) {
      const dummyAngle = (x(dummy.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
      g.selectAll('.grid-value-label')
        .data(gridValues)
        .enter()
        .append('text')
        .attr('class', 'grid-value-label')
        .attr('x', d => Math.cos(dummyAngle) * y(d))
        .attr('y', d => Math.sin(dummyAngle) * y(d) + 5)
        .attr('text-anchor', 'start')
        .attr('alignment-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#666')
        .text(d => d);
    }

    // center circle & text
    g.append('circle')
      .attr('r', 30)
      .attr('fill', '#e0e0e0');
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', '20px')
      .attr('fill', '#999')
      .text(data.length - 1);

  }, [items, width, ht]);

  return (
    <Wrapper className='graph' id={id}>
      <svg ref={ref} />
    </Wrapper>
  );
};

export default RadialBarChart;
